import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DateUtils } from '@vectord/utils';
import pluralize from 'pluralize';

import {
  AdjacentCoursePauseRequestButton,
  ADJACENT_COURSE_PAUSE_REQUEST_TYPE,
  CoursePauseBatchLecturesList,
  CoursePauseLearnerLecturesList,
  ManageModalBadge,
  ManageStageSuperBatchSelect,
  selectedRequestSelector,
  selectedSbatLectureSelector,
  selectedSuperBatchSelector,
  setSelectedSbatLecture,
  setSelectedSuperBatch,
} from 'features/courseResume';
import ScheduleResumeButton from '../ScheduleResumeButton';
import toDDMMMYYYYHHMM from 'utils/toDDMMMYYYYHHMM';
import styles from './CoursePauseModalManageStage.module.scss';

function CoursePauseModalManageStage() {
  const dispatch = useDispatch();
  const selectedSuperBatch = useSelector(selectedSuperBatchSelector);
  const selectedRequest = useSelector(selectedRequestSelector);
  const selectedSbatLecture = useSelector(selectedSbatLectureSelector);
  const scheduledResumeDate = selectedSbatLecture
    ? toDDMMMYYYYHHMM(selectedSbatLecture?.date_of_topic)
    : 'N/A';
  const {
    duration_of_pause: durationOfPause,
    pause_date: pauseDate,
    expected_resume_date: resumeDate,
    source_batch: sourceBatch,
    mentee,
    meta,
  } = selectedRequest;
  const { super_batch: sourceSuperBatch } = sourceBatch;
  const {
    suggested_sb_name: suggestedSuperBatchName,
    suggested_sb_id: suggestedSuperBatchId,
  } = meta || {};

  const handleLectureSelect = useCallback(
    (lecture) => {
      dispatch(setSelectedSbatLecture(lecture));
    },
    [dispatch]
  );

  useEffect(() => {
    if (suggestedSuperBatchId) {
      dispatch(
        setSelectedSuperBatch({
          id: suggestedSuperBatchId,
          name: suggestedSuperBatchName,
        })
      );
    }
  }, [dispatch, suggestedSuperBatchId, suggestedSuperBatchName]);

  return (
    <div className={styles.root}>
      <div className="h4 primary bold m-b-xs">MANAGE RESUME</div>
      <ManageStageSuperBatchSelect selectedSuperBatch={selectedSuperBatch} />
      <div className={styles.listSection}>
        <div className={styles.lectureList}>
          <div className="h6">CURRENT</div>
          <CoursePauseLearnerLecturesList
            superBatch={sourceSuperBatch}
            request={selectedRequest}
            referenceDate={pauseDate}
          />
        </div>
        <div className={styles.lectureList}>
          {selectedSuperBatch && (
            <>
              <div className="h6">NEW</div>
              <CoursePauseBatchLecturesList
                superBatch={selectedSuperBatch}
                coursePauseRequest={selectedRequest}
                referenceDate={Math.max(new Date(resumeDate), new Date())}
                isSelectable
                onSelect={handleLectureSelect}
                selectedLecture={selectedSbatLecture}
              />
            </>
          )}
        </div>
      </div>
      <div className={styles.dateSection}>
        <ManageModalBadge
          heading="Learner Email"
          data={mentee.user.email}
        />
        <ManageModalBadge
          heading="Paused From"
          data={DateUtils.toDDMMMYYYY(pauseDate)}
        />
        <ManageModalBadge
          heading="Pause Duration"
          data={pluralize('Month', durationOfPause, true)}
        />
        <ManageModalBadge
          heading="Expected Resume Date"
          data={DateUtils.toDDMMMYYYY(resumeDate)}
        />
        <ManageModalBadge
          heading="Scheduled Resume Date"
          data={scheduledResumeDate}
        />
      </div>
      <ScheduleResumeButton />
      <div className="row justify-c align-c m-t-s">
        <AdjacentCoursePauseRequestButton
          type={ADJACENT_COURSE_PAUSE_REQUEST_TYPE.previous}
        />
        <AdjacentCoursePauseRequestButton
          type={ADJACENT_COURSE_PAUSE_REQUEST_TYPE.next}
        />
      </div>
    </div>
  );
}

export default CoursePauseModalManageStage;
